import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// DS: tags = fundo "subtle" + texto "fg" legível. Cores via `--theme-*` (sistema
// único, dinâmico/white-label) — SEM `dark:` (o dark é resolvido pelos valores).
const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-[var(--theme-primary-background)] text-[var(--theme-primary)]",
        secondary:
          "border-[var(--theme-card-border)] bg-[var(--theme-surface-sunken)] text-[var(--theme-card-text)]",
        destructive:
          "border-transparent bg-[var(--theme-error-subtle)] text-[var(--theme-error-fg)]",
        outline:
          "border-[var(--theme-card-border)] bg-transparent text-[var(--theme-card-text)]",
        success:
          "border-transparent bg-[var(--theme-success-subtle)] text-[var(--theme-success-fg)]",
        warning:
          "border-transparent bg-[var(--theme-warning-subtle)] text-[var(--theme-warning-fg)]",
        info:
          "border-transparent bg-[var(--theme-info-subtle)] text-[var(--theme-info-fg)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const dotColorMap: Record<string, string> = {
  default: 'bg-[var(--theme-primary)]',
  secondary: 'bg-[var(--theme-muted-text)]',
  destructive: 'bg-[var(--theme-error-color)]',
  outline: 'bg-[var(--theme-muted-text)]',
  success: 'bg-[var(--theme-success-color)]',
  warning: 'bg-[var(--theme-warning-color)]',
  info: 'bg-[var(--theme-info-color)]',
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Show a colored dot indicator before the text */
  dot?: boolean;
}

function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  const variantKey = variant || 'default';
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full mr-1.5 flex-shrink-0',
            dotColorMap[variantKey]
          )}
        />
      )}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
